import React, { useState, useEffect, useCallback } from 'react';

const RedeemSettingsTab = ({ token, API_URL, companyProfile, showToast }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Form States
    const [rewardType, setRewardType] = useState('orders');
    const [ordersThreshold, setOrdersThreshold] = useState(100);
    const [amountThreshold, setAmountThreshold] = useState(100);
    const [pointsAwarded, setPointsAwarded] = useState(50);
    const [pointValue, setPointValue] = useState(1);
    const [dobDiscountValue, setDobDiscountValue] = useState(0);
    const [dobDiscountType, setDobDiscountType] = useState('percentage');

    // Fetch current settings
    const loadSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/redeem/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success && json.data) {
                const d = json.data;
                setRewardType(d.reward_type || 'orders');
                setOrdersThreshold(parseInt(d.orders_threshold || 100));
                setAmountThreshold(parseFloat(d.amount_threshold || 100));
                setPointsAwarded(parseInt(d.points_awarded || 50));
                setPointValue(parseFloat(d.point_value || 1));
                setDobDiscountValue(parseFloat(d.dob_discount_value || 0));
                setDobDiscountType(d.dob_discount_type || 'percentage');
            }
        } catch (err) {
            console.error('Error loading redeem settings:', err);
            showToast('Failed to load redeem settings', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [API_URL, token, showToast]);

    useEffect(() => {
        if (token && companyProfile?.id === 38) {
            loadSettings();
        }
    }, [token, companyProfile, loadSettings]);

    // Handle Form Submit
    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                reward_type: rewardType,
                orders_threshold: parseInt(ordersThreshold),
                amount_threshold: parseFloat(amountThreshold),
                points_awarded: parseInt(pointsAwarded),
                point_value: parseFloat(pointValue),
                dob_discount_value: parseFloat(dobDiscountValue),
                dob_discount_type: dobDiscountType
            };

            const res = await fetch(`${API_URL}/api/redeem/settings`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const json = await res.json();
            if (json.success) {
                showToast('Redeem settings saved successfully!', 'success');
            } else {
                showToast(json.message || 'Failed to save settings', 'error');
            }
        } catch (err) {
            console.error('Error saving redeem settings:', err);
            showToast('Failed to save settings', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
                <span className="ml-3 text-gray-500 font-medium">Loading settings...</span>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Form Configuration */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-lg p-6 expand-in" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-50">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 shadow-sm">
                        <i className="fas fa-gift text-lg"></i>
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Redeem Settings</h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Configure points conversion values, customer accrual models, and birthday gifts.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    {/* Strategy Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Rewards Earning Strategy</label>
                        <select 
                            value={rewardType}
                            onChange={(e) => setRewardType(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-gray-800"
                        >
                            <option value="orders">Orders Completed (Every X orders earns points)</option>
                            <option value="amount">Spent Amount (Every X rupees spent earns points)</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Threshold Settings */}
                        {rewardType === 'orders' ? (
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Orders Count Threshold</label>
                                <div className="relative">
                                    <input 
                                        type="number"
                                        min="1"
                                        value={ordersThreshold}
                                        onChange={(e) => setOrdersThreshold(e.target.value)}
                                        required
                                        className="w-full pl-4 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-gray-800"
                                        placeholder="e.g. 100"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">orders</span>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Spent Amount Threshold</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">₹</span>
                                    <input 
                                        type="number"
                                        min="1"
                                        step="0.01"
                                        value={amountThreshold}
                                        onChange={(e) => setAmountThreshold(e.target.value)}
                                        required
                                        className="w-full pl-8 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-gray-800"
                                        placeholder="e.g. 100.00"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">spent</span>
                                </div>
                            </div>
                        )}

                        {/* Points Awarded */}
                        <div>
                            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Points Awarded per Threshold</label>
                            <div className="relative">
                                <input 
                                    type="number"
                                    min="1"
                                    value={pointsAwarded}
                                    onChange={(e) => setPointsAwarded(e.target.value)}
                                    required
                                    className="w-full pl-4 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-gray-800"
                                    placeholder="e.g. 50"
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">points</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-6 border-gray-50">
                        {/* Point Value (Redemption Conversion) */}
                        <div>
                            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Value of 1 Redeem Point</label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600 font-bold">₹</span>
                                <input 
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={pointValue}
                                    onChange={(e) => setPointValue(e.target.value)}
                                    required
                                    className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-emerald-600 font-bold"
                                    placeholder="e.g. 1.00"
                                />
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1">Ex: If set to 0.50, then 100 points = ₹50 discount at checkout.</p>
                        </div>

                        {/* DOB Discount Value */}
                        <div>
                            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Date of Birth (Birthday) Discount</label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    {dobDiscountType === 'fixed' && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₹</span>}
                                    <input 
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={dobDiscountValue}
                                        onChange={(e) => setDobDiscountValue(e.target.value)}
                                        required
                                        className={`w-full py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-gray-800 ${dobDiscountType === 'fixed' ? 'pl-8' : 'pl-4'}`}
                                        placeholder="e.g. 10"
                                    />
                                    {dobDiscountType === 'percentage' && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>}
                                </div>
                                <select 
                                    value={dobDiscountType}
                                    onChange={(e) => setDobDiscountType(e.target.value)}
                                    className="px-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 outline-none transition-all font-medium text-gray-700 bg-gray-50"
                                >
                                    <option value="percentage">Percent</option>
                                    <option value="fixed">Fixed (₹)</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-4 border-t border-gray-50">
                        <button 
                            type="submit"
                            disabled={isSaving}
                            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-3 px-8 rounded-xl shadow-md hover:shadow-lg transition-all active:scale-98 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-check-circle"></i>
                                    <span>Save Configuration</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Right: Live Preview Panel */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl text-white p-6 shadow-xl flex flex-col justify-between border border-slate-700 expand-in" style={{ animationDelay: '0.4s' }}>
                <div>
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-wider mb-4">Loyalty Program Preview</h3>
                    
                    {/* Accrual Card */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                        <p className="text-xs text-slate-300 font-medium mb-1">Earning Rule</p>
                        <p className="text-base font-bold text-emerald-400">
                            {rewardType === 'orders' 
                                ? `Every ${ordersThreshold} orders earns ${pointsAwarded} points`
                                : `Every ₹${amountThreshold} spent earns ${pointsAwarded} points`}
                        </p>
                    </div>

                    {/* Value Card */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                        <p className="text-xs text-slate-300 font-medium mb-1">Redemption Conversion</p>
                        <p className="text-xl font-black text-white">
                            1 Point = <span className="text-amber-400">₹{parseFloat(pointValue).toFixed(2)}</span>
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1">
                            Example: 100 points awards a client ₹{(100 * pointValue).toFixed(0)} discount
                        </p>
                    </div>

                    {/* Birthday Gift Card */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <p className="text-xs text-slate-300 font-medium mb-1">Birthday Reward</p>
                        <p className="text-base font-bold text-pink-400">
                            {parseFloat(dobDiscountValue) > 0 
                                ? `${dobDiscountType === 'percentage' ? `${dobDiscountValue}%` : `₹${dobDiscountValue}`} Birthday Discount`
                                : 'No Birthday Discount set'}
                        </p>
                        <p className="text-[10px] text-slate-400 mt-1">
                            Applied automatically to active tables on customer's DOB.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RedeemSettingsTab;
